import { Formik, FormikHelpers } from 'formik';
import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { BsQuestionCircle } from 'react-icons/bs';
import * as yup from 'yup';
import type { ObjectSchema } from 'yup';

import BayModal from '../../BayModal';
import BaySwitch from '../../BaySwitch';
import BayTooltip from '../../BayTooltip';
import {
	regexIsValidPathTest,
	regexHasForbiddenCharTest,
} from '../../utilities/regex';

const regexIsValidPath = new RegExp(regexIsValidPathTest);
const regexHasForbiddenChar = new RegExp(regexHasForbiddenCharTest);

interface FormValues {
	folderName: string;
	isHidden: boolean;
	presentOnHomepage: boolean;
}

interface EditData {
	_id?: string;
	name?: string;
	isHidden?: boolean;
	presentOnHomepage?: boolean;
}

interface CreateNewFolderModalProps {
	t: (key: string) => string;
	title: string;
	showModal: boolean;
	isReadyToShare?: boolean;
	onCloseFunc: () => void;
	buttonText?: string;
	onSubmitFunc: (values: FormValues & { isEditMode: boolean; editData: EditData | null }) => void;
	formSchema?: ObjectSchema<FormValues>;
	initialValues?: Partial<FormValues>;
	isEditMode?: boolean;
	editData?: EditData | null;
}

// Default values for a new folder
const DEFAULT_VALUES: FormValues = {
	folderName: '',
	isHidden: false,
	presentOnHomepage: true,
};

// Helper function to normalize editData into form values shape
const mapEditDataToValues = (editData: EditData | null | undefined): FormValues => {
	if (!editData) {
		return DEFAULT_VALUES;
	}

	return {
		folderName: editData?.name || '',
		isHidden: editData?.isHidden ?? false,
		presentOnHomepage: editData?.presentOnHomepage ?? false,
	};
};

const CreateNewFolderModal: React.FC<CreateNewFolderModalProps> = ({
	t,
	title,
	showModal,
	isReadyToShare = false,
	onCloseFunc,
	buttonText,
	onSubmitFunc,
	formSchema,
	initialValues,
	isEditMode = false,
	editData = null,
}) => {
	// Initialize form values based on edit mode or initial values
	const getInitialValues = (): FormValues => {
		if (isEditMode && editData) {
			return mapEditDataToValues(editData);
		}
		return { ...DEFAULT_VALUES, ...initialValues };
	};

	const [formFieldValues, setFormFieldValues] = useState<FormValues>(getInitialValues());

	// Update form values when editData changes or modal opens/closes
	useEffect(() => {
		if (showModal) {
			setFormFieldValues(getInitialValues());
		}
	}, [isEditMode, editData, showModal, initialValues]);

	const schema = yup.object().shape({
		folderName: yup
			.string()
			.min(1, t('folderNameInvalidErrorText'))
			.max(64, t('folderNameInvalidErrorText'))
			.required(t('folderNameRequiredErrorText'))
			.test(
				'pathName',
				t('folderNameInvalidErrorText'),
				(value) =>
					!!value && !regexHasForbiddenChar.test(value) && regexIsValidPath.test(value),
			),
		isHidden: yup.boolean(),
		presentOnHomepage: yup.boolean(),
	});

	const handleOnSubmit = (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
		onSubmitFunc({ ...values, isEditMode, editData });

		if (!isEditMode) {
			setFormFieldValues({ ...DEFAULT_VALUES, ...initialValues });
			resetForm();
		}
		onCloseFunc();
	};

	const handleIsEnterKey = (event: React.KeyboardEvent) => {
		if (event.keyCode === 13 || event.key === 'Enter') {
			event.stopPropagation();
			event.preventDefault();
			return;
		}
	};

	const handleInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		handleIsEnterKey(event as unknown as React.KeyboardEvent);
		setFormFieldValues({
			...formFieldValues,
			folderName: event.target.value,
		});
	};

	return (
		<div>
			<Formik
				key={isEditMode && editData ? editData?._id : 'create'}
				validateOnBlur
				validateOnChange
				enableReinitialize
				initialValues={formFieldValues}
				validationSchema={formSchema ?? schema}
				onSubmit={handleOnSubmit}
			>
				{({
					handleSubmit,
					isSubmitting,
					values,
					touched,
					errors,
					resetForm,
				}) => {
					// Calculate whether the form has changes
					const hasChanges = (() => {
						if (isEditMode && editData) {
							const editValues = mapEditDataToValues(editData);
							return editValues.folderName !== values.folderName ||
    				            editValues.isHidden !== values.isHidden ||
    				            editValues.presentOnHomepage !== values.presentOnHomepage;
						}
						if (!isEditMode && initialValues?.folderName) {
							return initialValues.folderName !== values.folderName;
						}
						return true; // Enable button by default
					})();
					return (
						<BayModal
							backdrop="static"
							buttonText={t('save')}
							centered
							closeButton
							footer={
								<>
									<div>
										<Button
											className="cancel-button"
											id="create_new_folder-cancel_btn"
											variant="outline-secondary"
											onClick={() => {
												if (!isEditMode) {
													setFormFieldValues({ ...DEFAULT_VALUES, ...initialValues });
												}
												onCloseFunc();
												resetForm();
											}}
										>
											{t('cancel')}
										</Button>
									</div>
									<div className="text-right">
										<Button
											className="ms-3"
											disabled={
												isSubmitting ||
												!!errors?.folderName ||
												!hasChanges
											}
											id="create_new_folder-submit_btn"
											onClick={() => handleSubmit()}
										>
											{buttonText|| (isEditMode ? t('save') : t('create'))}
										</Button>
									</div>
								</>
							}
							height="100%"
							noPadding
							show={showModal}
							title={title}
							onHideFunc={() => {
								if (!isEditMode) {
									setFormFieldValues({ ...DEFAULT_VALUES, ...initialValues });
								}
								onCloseFunc();
								resetForm();
							}}
						>
							<Form className="p-5 pt-3 pb-4" id="create_new_folder" onSubmit={handleSubmit}>
								<Form.Group className="mb-4">
									<Form.Label className="required">
										{t('folderName')}
									</Form.Label>
									<Form.Control
										required
										autoComplete="off"
										className={!errors?.folderName ? '' : 'is-invalid'}
										id="create_new_folder-folder_name"
										maxLength={64}
										placeholder={t('folderNamePlaceholder')}
										type="text"
										value={formFieldValues.folderName}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleInputChanged(event)}
										onKeyDown={(event) => handleIsEnterKey(event)}
									/>
									{touched?.folderName && errors?.folderName && (
										<Form.Control.Feedback className="d-flex" type="invalid">
											<div className="alert alert-danger mt-2" role="alert">
												<h2>{t('invalidFolderName')}</h2>
												<p>
													{t('invalidFolderNameContent')} : {errors?.folderName}
												</p>
												<ul>
													<li>Must have a minimum of 1 character.</li>
													<li>{'Must not contain + = | * ? / \\ : < > $'}</li>
													<li>Must not start or end with .</li>
													<li>Must not start or end with a space</li>
													<li>Must not contain double spaces</li>
												</ul>
											</div>
										</Form.Control.Feedback>
									)}
								</Form.Group>
								<Form.Group className="d-flex align-items-center">
									<BaySwitch
										checked={!values.isHidden}
										id="create_new_folder-folder_visibility"
										label={t('generalDisplay')}
										onChange={() => {
											const newObj: FormValues = {
												...formFieldValues,
												isHidden: !values.isHidden,
											};

											if (isReadyToShare && values.isHidden) {
												newObj.presentOnHomepage = false;
											}

											setFormFieldValues(newObj);
										}}
									/>
									<div className="mb-3">
										<BayTooltip
											overlay={<div dangerouslySetInnerHTML={{ __html: t('generalVisibilityTooltip') }} />}
											placement="top"
										>
											<BsQuestionCircle className="ms-2 text-muted" size={16} />
										</BayTooltip>
									</div>
								</Form.Group>
								{isReadyToShare && (
									<Form.Group className="d-flex align-items-center">
										<BaySwitch
											checked={values.presentOnHomepage}
											disabled={values.isHidden}
											id="create_new_folder-folder_homepage"
											label={t('showOverviewPage')}
											onChange={() =>
												setFormFieldValues({
													...formFieldValues,
													presentOnHomepage: !values.presentOnHomepage,
												})
											}
										/>
										<div className="mb-3">
											<BayTooltip
												overlay={<div dangerouslySetInnerHTML={{ __html: t('overviewTooltip') }} />}
												placement="top"
											>
												<BsQuestionCircle className="ms-2 text-muted" size={16} />
											</BayTooltip>
										</div>
									</Form.Group>
								)}
							</Form>
						</BayModal>
					);
				}}
			</Formik>
		</div>
	);
};

export default CreateNewFolderModal;
